import { useToast } from '@1trading/react/hooks/use-toast';
import { ArrowRightIcon } from 'lucide-react';
import type React from 'react';
import { useCallback } from 'react';
import { type FieldValues, useFormContext } from 'react-hook-form';
import { z } from 'zod';

import { PageHeadingButton } from '@/components/ui/page-heading';
import { usePromise } from '@/hooks/use-promise';

export type GoToSummaryButtonProps<T extends FieldValues> = {
  disabled?: boolean;
  onIncompleteSection?: () => void | Promise<void>;
  onSubmit: (data: T) => void | Promise<void>;
};

type GoToSummaryButtonBaseProps<T extends FieldValues> = React.PropsWithChildren<GoToSummaryButtonProps<T> & { incompleteSectionMessage: string }>;

const GoToSummaryButton = <T extends FieldValues>({ children, disabled, incompleteSectionMessage, onIncompleteSection, onSubmit }: GoToSummaryButtonBaseProps<T>) => {
  const form = useFormContext<T>();
  const { toast } = useToast();

  const handleGoToSummary = useCallback(async () => {
    if (!(await form.trigger())) return;

    try {
      await onSubmit(form.getValues());
    } catch (error) {
      // The section's conversion parses every step before saving, so a later step the edit opened up fails here
      if (!(error instanceof z.ZodError)) throw error;

      toast({ description: incompleteSectionMessage });
      await onIncompleteSection?.();
    }
  }, [form, incompleteSectionMessage, onIncompleteSection, onSubmit, toast]);

  const { isPending, handler } = usePromise(handleGoToSummary);

  return (
    <PageHeadingButton className="ml-auto" floatingInPage="right" disabled={disabled || isPending} onClick={handler}>
      {children}
      <ArrowRightIcon className="size-4" />
    </PageHeadingButton>
  );
};
GoToSummaryButton.displayName = 'GoToSummaryButton';

export default GoToSummaryButton;
